import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  type TemplateRef,
  viewChild,
} from '@angular/core';

import { IonModal } from '@ionic/angular/standalone';
import { NgpMenu, NgpMenuItem } from 'ng-primitives/menu';
import { type NgpOverlayTemplateContext } from 'ng-primitives/portal';
import { twMerge } from 'tailwind-merge';

import { DeviceService } from '@core/device';
import { I18nPipe } from '@core/i18n';

import { IconComponent } from '@shared/icon';

import {
  menuGroupTitleCva,
  menuItemCva,
  menuLabelCva,
  menuPanelCva,
  menuSeparatorCva,
  menuSheetCva,
  menuTrailingCva,
} from './menu.cva';
import {
  MENU_SHEET_BREAKPOINT_VALUES,
  MENU_SHEET_BREAKPOINTS,
  type MenuActionItem,
  type MenuItem,
  type MenuRow,
} from './menu.types';
import { buildRows, moveSheetFocus, SHEET_ITEM_SELECTOR } from './menu.util';

@Component({
  selector: 'ml-menu',
  imports: [NgpMenu, NgpMenuItem, NgTemplateOutlet, IonModal, IconComponent, I18nPipe],
  host: { class: 'contents' },
  templateUrl: './menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent {
  readonly items = input<readonly MenuItem[]>([]);
  readonly extraClass = input<string>('', { alias: 'panelClass' });
  readonly sheetOnMobile = input(true, { transform: booleanAttribute });
  /** Keep focus on an input inside the trigger (combobox). Default menus still trap focus. */
  readonly retainTriggerFocus = input(false, { transform: booleanAttribute });
  readonly itemSelect = output<string>();

  readonly panelTemplate = viewChild<TemplateRef<NgpOverlayTemplateContext<unknown>>>('panel');

  private readonly device = inject(DeviceService);
  protected readonly sheetOpen = signal(false);

  protected readonly sheetBreakpoints = MENU_SHEET_BREAKPOINTS;
  protected readonly sheetBreakpointValues = MENU_SHEET_BREAKPOINT_VALUES;
  protected readonly itemClass = menuItemCva;
  protected readonly labelClass = menuLabelCva;
  protected readonly separatorClass = menuSeparatorCva;
  protected readonly panelTrailingClass = menuTrailingCva();
  protected readonly sheetTrailingClass = menuTrailingCva({ surface: 'sheet' });
  protected readonly panelGroupTitleClass = menuGroupTitleCva();
  protected readonly sheetGroupTitleClass = menuGroupTitleCva({ surface: 'sheet' });
  readonly usesSheet = computed(() => this.sheetOnMobile() && this.device.isMobileUI());

  protected readonly panelClass = computed(() => twMerge(menuPanelCva, this.extraClass()));
  protected readonly sheetClass = computed(() => twMerge(menuSheetCva, this.extraClass()));
  protected readonly rows = computed<readonly MenuRow[]>(() => buildRows(this.items()));

  openSheet(): void {
    this.sheetOpen.set(true);
  }

  closeSheet(): void {
    this.sheetOpen.set(false);
  }

  protected onSelect(item: MenuActionItem): void {
    this.itemSelect.emit(item.value);
    this.closeSheet();
  }

  protected onTitleAction(value: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.itemSelect.emit(value);
  }

  protected onSheetPresented(event: Event): void {
    const root = event.target;
    if (!(root instanceof HTMLElement)) return;

    root.querySelector<HTMLButtonElement>(SHEET_ITEM_SELECTOR)?.focus();
  }

  protected onSheetKeydown(event: KeyboardEvent): void {
    const root = event.currentTarget;
    if (!(root instanceof HTMLElement)) return;

    if (moveSheetFocus(root, event.key)) event.preventDefault();
  }
}
